import { Component, OnDestroy, OnInit } from '@angular/core';
import { ScaleDetails } from '../../../shared/ontology/model/scale-details';
import { SubscriptionsManager } from '../../../shared/util/subscriptions-manager';
import { JhiEventManager, JhiLanguageService } from '../../../shared/core-utils';
import { ScaleService } from '../../../shared/ontology/service/scale.service';
import { ScaleSearchRequest } from '../../../shared/ontology/model/scale-search-request.model';
import { ColumnFilterComponent, FilterType } from '../../../shared/column-filter/column-filter.component';
import { ColumnFilterTransitionEventModel } from '../../../shared/column-filter/column-filter-transition-event.model';
import { MatchType } from '../../../shared/column-filter/column-filter-text-with-match-options-component';
import { SORT_PREDICATE_NONE } from '../../../germplasm-manager/germplasm-search-resolve-paging-params';
import { finalize } from 'rxjs/internal/operators/finalize';
import { HttpResponse } from '@angular/common/http';
import { Variable } from '../../../shared/ontology/model/variable';
import { DropdownOptionData } from '../../../shared/column-filter/column-filter-dropdown-option-data';
import { NameType } from '../../../shared/germplasm/model/name-type.model';
import { OntologyService } from '../../../shared/ontology/service/ontology.service';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
    selector: 'jhi-scales-pane',
    templateUrl: 'scales-pane.component.html'
})
export class ScalesPaneComponent implements OnInit, OnDestroy {
    static readonly COLUMN_FILTER_EVENT_NAME = 'searchColumnFiltersChanged';
    protected readonly NAME_TEXT_LIMIT = 200;
    protected readonly DESCRIPTION_TEXT_LIMIT = 1024;
    COLUMN_FILTER_EVENT_NAME = ScalesPaneComponent.COLUMN_FILTER_EVENT_NAME;

    itemsPerPage = 20;

    ColumnLabels = ColumnLabels;

    scales: ScaleDetails[];
    searchRequest: ScaleSearchRequest;

    subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();

    currentSearch: string;
    totalItems: number;
    page: number;
    previousPage: number;
    predicate: any;
    reverse: boolean;

    isLoading: boolean;
    scaleFilters: any;

    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private jhiLanguageService: JhiLanguageService,
                private eventManager: JhiEventManager,
                private scaleService: ScaleService,
                private ontologyService: OntologyService) {
        this.page = 1;
        this.totalItems = 0;
        this.predicate = ColumnLabels.NAME;
        this.reverse = true;
        this.searchRequest = new ScaleSearchRequest();
    }

    get filters() {
        return this.scaleFilters;
    }

    set filters(filters) {
        this.scaleFilters = filters;
    }

    get request() {
        return this.searchRequest;
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.filters = this.getInitialFilters();
        this.registerColumnFiltersChanged();
        this.registerEvents();
        this.loadAll(this.request);
    }

    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.loadAll(this.request);
    }

    registerEvents(): any {
        this.subscriptionsManager.add = this.eventManager.subscribe('scalePaneViewChanged', (event) => {
            this.loadAll(this.request);
        });
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.transition();
        }
    }

    transition() {
        this.router.navigate(['./'], {
            queryParamsHandling: 'merge',
            queryParams: {
                page: this.page,
                size: this.itemsPerPage,
                search: this.currentSearch,
                sort: this.getSort()
            },
            relativeTo: this.activatedRoute
        });
        this.loadAll(this.request);
    }

    getSort() {
        if (this.predicate === SORT_PREDICATE_NONE) {
            return '';
        }
        return [this.predicate + ',' + (this.reverse ? 'asc' : 'desc')];
    }

    loadAll(request: ScaleSearchRequest) {
        this.isLoading = true;
        this.scaleService.searchScales(request,
            {
                page: this.page - 1,
                size: this.itemsPerPage,
                sort: this.getSort()
            }).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe(
            (res: HttpResponse<Variable[]>) => this.onSuccess(res.body, res.headers)
        );
    }

    onClearSort($event) {
        $event.preventDefault();
        this.clearSort();
        this.transition();
    }

    trackId(index: number, item: ScaleDetails) {
        return item.id;
    }

    private registerColumnFiltersChanged() {
        this.subscriptionsManager.add = this.eventManager.subscribe(ScalesPaneComponent.COLUMN_FILTER_EVENT_NAME, (event: ColumnFilterTransitionEventModel) => {
            this.resetTable();
        });
    }

    private getInitialFilters() {
        const me = this;
        return [
            {
                key: 'nameFilter', name: 'column-filter.scale.name', placeholder: 'column-filter.search.text.placeholder', type: FilterType.TEXT_WITH_MATCH_OPTIONS,
                matchType: MatchType.STARTSWITH, default: true
            },
            {
                key: 'description', name: 'column-filter.description', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT
            },
            {
                key: 'dataTypeIds', name: 'column-filter.data.type', type: FilterType.DROPDOWN, values: this.getDataTypesOptions(), multipleSelect: true,
                transform(req) {
                    ColumnFilterComponent.transformDropdownFilter(this, req);
                },
                reset(req) {
                    ColumnFilterComponent.resetDropdownFilter(this, req);
                },
            }
        ];
    }

    private async getDataTypesOptions(): Promise<DropdownOptionData[]> {
        const dataTypes = await this.ontologyService.getDataTypes().toPromise();
        return dataTypes.map((nameType: NameType) => {
            return {
                id: nameType.id.toString(),
                text: nameType.name
            };
        });
    }

    private clearSort() {
        this.predicate = ColumnLabels.NAME;
        this.reverse = true;
    }

    private onSuccess(data: Variable[], headers) {
        this.totalItems = headers.get('X-Total-Count');
        this.scales = data;
    }

}

export enum ColumnLabels {
    'NAME' = 'scaleName',
    'DESCRIPTION' = 'scaleDefinition',
    'DATA_TYPE' = 'dataTypeId'
}
